import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { mask } from 'superstruct';

import {
	useGoogleMyBusinessTemplateQuery,
	useMutationAddGoogleMyBusiness,
} from '@adflow/frontend/feat-google-my-business';
import { ButtonWithLoading } from '@adflow/frontend/ui-buttons';
import { createErrorToast } from '@adflow/frontend/ui-toasts';
import { useDispatchNavAction } from '@adflow/frontend/util-router';
import { AddGMBTemplateDto } from '@adflow/shared/data-google-my-business';

import { stripTrailingTimestamp } from '../../GoogleAds/GoogleAdsTemplates/util';

import { goToEditGMBTemplate } from './GMBTemplateNavActions';

interface GMBDuplicateTemplateProps {
	templateId: number;
}

export function GMBDuplicateTemplate(props: GMBDuplicateTemplateProps) {
	const { templateId } = props;

	const { t } = useTranslation('common');

	const navigate = useDispatchNavAction();

	const { data: gmbTemplateData, isLoading } =
		useGoogleMyBusinessTemplateQuery(templateId);

	const { mutateAsync: addGMBTemplate, isPending } =
		useMutationAddGoogleMyBusiness();

	return (
		<ButtonWithLoading
			loading={isLoading || isPending}
			onClick={async () => {
				if (!gmbTemplateData) return;

				const suffix = ` - ${DateTime.now().toFormat('yyyy-LL-dd HH:mm:ss')}`;
				gmbTemplateData.name = `${stripTrailingTimestamp(gmbTemplateData.name)}${suffix}`;

				let maskedTemplate: AddGMBTemplateDto | undefined;

				try {
					maskedTemplate = mask(gmbTemplateData, AddGMBTemplateDto);
					maskedTemplate.id = undefined;
				} catch (err) {
					const errorMessage =
						err instanceof Error ? err.message : 'Unknown error';
					const splitError = errorMessage.split('--');
					const validationError =
						splitError.length > 1 && splitError[1]
							? splitError[1].trim()
							: errorMessage;

					createErrorToast(
						`Failed to duplicate template: ${t(validationError)}`
					);
				}

				if (maskedTemplate) {
					const result = await addGMBTemplate(maskedTemplate);
					navigate(goToEditGMBTemplate(result.id));
				}
			}}
		>
			{t('duplicate')}
		</ButtonWithLoading>
	);
}
